import { Component, OnInit, ViewChild, Inject, AfterViewInit, OnDestroy, ElementRef } from '@angular/core';
import { Observable, BehaviorSubject, Subscription, of, Subject, merge } from 'rxjs';
import { concatMap, map, distinctUntilChanged, tap, catchError, filter } from 'rxjs/operators';
import { GridDataSource } from '../../../shared/models/grid-data-source';
import * as moment from 'moment';
import { FeatureToggleService } from '../../../shared/services/feature-toggle/feature-toggle.service';
import { UserNotification, UserNotificationCommand } from '../../../shared/components/notification-bar/notification-bar.model';
import { SettingsService } from '../../../shared/services/settings/settings.service';
import * as _ from 'lodash';
import { CompanyBasicInfo } from '../../../services/company.model';
import { EntitlementsService } from '../../../entitlements/entitlements.service';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { HttpErrorResponse } from '@angular/common/http';
import { UkgModal } from '@ignite/angular';
import { SortEventData } from '../../pay-info/pay-info-table/ignite/web-components/sort-event';
import { PaginatorPageEvent, PaginatorPageEventRange } from '../../pay-info/pay-info-table/ignite/web-components/paginator-page-event';
import { CompanyService } from '../../../services/company.service';
import { PrenoteRequestService } from '../../../services/prenote-request.service';
import { CompanyInfoFiltersService } from '../company-info-filters/company-info-filters.service';
import { CompanyInfoFilters } from '../company-info-filters/company-info-filters.model';
import { Router } from '@angular/router';

export class PSGCompanyInfo {
  tenantId: string;
  companyId: string;
  legalName: string;
  companyCode: string;
  fein: string;
  lastPrenoteFileSent: Date;
  payGroups: string;
}

class CompanyDisplay {
  tenantId: string;
  companyId: string;
  legalName: string;
  companyCode: string;
  fein: string;
  lastPrenoteFileSent: Date;
  requestedFromPSG: boolean;
  payGroups: string;
}

@Component({
  selector: 'ssg-company-info-table',
  templateUrl: './company-info-table.component.html',
  styleUrls: ['./company-info-table.component.less']
})
export class CompanyInfoTableComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly maxRecordCount = 10000;
  public readonly defaultPrenoteFileSentDate = new Date('0001-01-01T00:00:00Z');

  dataSource: GridDataSource<PSGCompanyInfo>;

  rawData: PSGCompanyInfo[];
  private _rawDataTable: { [id: string]: PSGCompanyInfo };
  private _selectedCompanyTable: { [id: string]: boolean };
  private _selectedCompanyCount: number;
  private _cachedSelection: PSGCompanyInfo[];

  private _manualFilterUpdate: Subject<any>;

  private _sortSubject: BehaviorSubject<SortEventData>;
  private _pageSubject: BehaviorSubject<PaginatorPageEventRange>;

  private _notification: UserNotification = null;

  allColumns: string[];
  showFilters = false;
  sortKey: SortEventData;

  isSendingPrenote = false;
  records: PSGCompanyInfo[];
  public initialRowsPerPage = 25 as number;
  public rowsOptions = [10, 25, 50, 100];
  private _dataSubscription: Subscription;

  @ViewChild('companyInfoTableNotificationBar', { static: true, read: ElementRef }) notificationBarElement: ElementRef;
  @ViewChild('sendPrenoteResultsModal') sendPrenoteResultsModal: UkgModal;
  @ViewChild('sendPrenoteConfirmationModal') sendPrenoteConfirmationModal: UkgModal;

  loadingData = false;

  get selectedCompanyInfos(): PSGCompanyInfo[] {
    if (!this._cachedSelection) {
      const results: PSGCompanyInfo[] = [];
      for (const id in this._selectedCompanyTable) {
        if (this._selectedCompanyTable.hasOwnProperty(id) && this._selectedCompanyTable[id]) {
          const item = this._rawDataTable[id];
          if (item) {
            results.push(item);
          }
        }
      }

      this._cachedSelection = results;
    }

    return this._cachedSelection;
  }

  get dataCount(): number {
    return (this.rawData || []).length;
  }

  get selectedCompanyCount(): number {
    return this._selectedCompanyCount;
  }

  readonly userMessages = {
    applyFilter: { message: 'COMPANIES.APPLY_FILTER',
      type: 'user-info',
      severity: 'information',
      persistToStorage: true,
      distinct: true },
    moreRecords: { message: 'COMPANIES.MORE_RECORDS', type: 'status', severity: 'warning' },
    noMatchingRecords: { message: 'COMPANIES.NO_MATCHING_RECORDS', type: 'status', severity: 'information' },
    insufficientRights: { message: 'COMPANIES.SEND_PRENOTE_RESULT_INSUFFICIENT_RIGHTS', type: 'status', severity: 'important' },
    success: { message: 'COMPANIES.SUCCESSFUL_QUERY', type: 'status', severity: 'success' },
  };

  notificationSubject: Subject<UserNotificationCommand> = new Subject<UserNotificationCommand>();

  private _setStatusNotification(notif: string, params: any = null) {
    if (this._notification == null) {
      this._notification = new UserNotification(notif, params);
    }
  }

  private _publishNotification(notif: UserNotification): void {
    this.notificationSubject.next(new UserNotificationCommand('add', notif));
  }

  private _clearStatusNotification(): void {
    this.notificationSubject.next(new UserNotificationCommand('clearKey', this._notification));
    this._notification = null;
  }

  // Sorting Event in table
  handleSort(newKey: CustomEvent<SortEventData>): void {
    this._sortSubject.next((newKey as CustomEvent<SortEventData>).detail);
  }

  constructor(
    @Inject(CompanyService) private readonly _companyService: CompanyService,
    @Inject(PrenoteRequestService) private readonly _prenoteRequestService: PrenoteRequestService,
    @Inject(ScrollDispatcher) private readonly _scrollDispatcher: ScrollDispatcher,
    @Inject(CompanyInfoFiltersService) private readonly _filtersService: CompanyInfoFiltersService,
    private readonly router: Router,
    private readonly _entitlementsService: EntitlementsService,
    private readonly _featureToggleService: FeatureToggleService,
    private readonly settings: SettingsService
  ) {
  }

  ngOnInit() {
    this.allColumns = [
      'companySelected',
      'legalName',
      'companyCode',
      'fein',
      'payGroups',
      'lastPrenoteFileSent',
    ];

    this._filtersService.clearFilters();
    this.records = [];
    this._rawDataTable = {};
    this._resetSelection();

    // (filters + API data -> this._setRawData)
    const apiDataPipeline = this._initApiDataPipeline();

    this._sortSubject = new BehaviorSubject<SortEventData>({ key: 'legalName', isAsc: 'asc' });
    const sortPipeline = this._sortSubject.pipe(
      distinctUntilChanged((previous: SortEventData, current: SortEventData) =>
        previous.key === current.key && previous.isAsc === current.isAsc));

    this._pageSubject = new BehaviorSubject<PaginatorPageEventRange>({ start: 0, end: this.initialRowsPerPage - 1 });
    const pagePipeline = this._pageSubject.pipe(
      distinctUntilChanged((previous: PaginatorPageEventRange, current: PaginatorPageEventRange) =>
        previous.start === current.start && previous.end === current.end));

    this.dataSource = new GridDataSource(
      apiDataPipeline,
      pagePipeline,
      sortPipeline);

    this.dataSource.customSort = (data: PSGCompanyInfo[], sortEvent: SortEventData) => {
      if (sortEvent.key === 'none') {
        return data;
      } else if (sortEvent.key === 'legalName') {
        return this.dataSource.defaultSort(data, [{
          key: 'legalName',
          isAsc: sortEvent.isAsc
        }, {
          key: 'companyCode',
          isAsc: sortEvent.isAsc
        }]);
      } else if (sortEvent.key === 'companyCode') {
        return this.dataSource.defaultSort(data, [{
          key: 'companyCode',
          isAsc: sortEvent.isAsc
        }, {
          key: 'fein',
          isAsc: sortEvent.isAsc
        }]);
      }

      return this.dataSource.defaultSort(data, sortEvent);
    };

    this._dataSubscription = this.dataSource.connect().subscribe(data => this.records = data);


  }

  ngOnDestroy() {
    this._dataSubscription.unsubscribe();
    this.records = [];
  }

  ngAfterViewInit() {
    this._publishNotification(new UserNotification('applyFilter', null));
  }

  private _refreshData() {
    this._dataSubscription.unsubscribe();
    this._dataSubscription = this.dataSource.connect().subscribe(data => this.records = data);
  }

  private _initApiDataPipeline(): Observable<PSGCompanyInfo[]> {

    this._manualFilterUpdate = new Subject<void>();

    const companyById: { [key: string]: CompanyDisplay } = {};
    let results: PSGCompanyInfo[] = [];

    let lastFilters: CompanyInfoFilters = null;
    const sendManualFilters = function() {
      // Replicate a filter updated event any time we get an update to the
      // manualFilterUpdate subject, just copying the last real filters
      // (which may be null, but we filter out null filters in the first step below).
      return of(lastFilters);
    };
    const manualFiltersUpdate = this._manualFilterUpdate.pipe(sendManualFilters);

    const filtersSource = merge(this._filtersService.getFilters(), manualFiltersUpdate);
    return filtersSource.pipe(
      filter(filters => filters != null),
      tap(() => this._clearStatusNotification()),
      map((filters: CompanyInfoFilters) => {
        lastFilters = filters;
        return this._companyService.getPSGCompanyInfos(
          this.settings.tenantId,
          filters.payGroups,
          filters.companies,
          filters.companyCodes,
          filters.feins,
          1,
          this.maxRecordCount).pipe(
            catchError(error => {
              this._handleError(error);
              return of([] as CompanyBasicInfo[]);
            })
          );
      }),
      concatMap(data => data),
      map(companies => {
        results = [];

        if (companies.length >= this.maxRecordCount) {
          this._setStatusNotification('moreRecords', { maxRecords: this.maxRecordCount });
        }
        for (const company of companies) {
          const displayData = {
            tenantId: company.tenantId,
            companyId: company.companyId,
            legalName: company.legalName,
            companyCode: company.integration.cmpCode,
            fein: company.fein,
            lastPrenoteFileSent: moment('0001-01-01').utc().toDate(),
            requestedFromPSG: false,
            payGroups: company.payGroups.join(', ')
          } as CompanyDisplay;

          companyById[displayData.companyId] = displayData;
          results.push(displayData);
        }
      }),
      map(() => this._prenoteRequestService.hasPrenote(
            this.settings.tenantId,
            Object.keys(companyById))),
      concatMap(prenotes => prenotes),
      map( prenoteInfos => {
        for (const prenoteInfo of prenoteInfos) {
          if (prenoteInfo.companyId in companyById) {
              companyById[prenoteInfo.companyId].lastPrenoteFileSent = moment(prenoteInfo.createdUtcDate).toDate() ;
              companyById[prenoteInfo.companyId].requestedFromPSG = prenoteInfo.requestedFromPSGFlag;
          }
        }

        if (lastFilters.prenoteFileSent === null || lastFilters.prenoteFileSent === undefined) {
          return results;
        }

        const  companiesFilteredByPrenoteSent: PSGCompanyInfo[] = [];
        for (const company of results) {
          const companyHasPrenote = !moment(company.lastPrenoteFileSent).isSame('0001-01-01', 'day');
          if ((companyHasPrenote && lastFilters.prenoteFileSent === true)
          || (!companyHasPrenote && lastFilters.prenoteFileSent === false)) {
              companiesFilteredByPrenoteSent.push(company);
}
        }
        return companiesFilteredByPrenoteSent;
      }),
      tap((companiesFilteredByPrenoteSent) => this._setRawData(companiesFilteredByPrenoteSent))
    );
  }

  private _setRawData(value: PSGCompanyInfo[]) {
    this.rawData = value;
    this._rawDataTable = {};
    for (const data of this.rawData) {
      this._rawDataTable[data.companyId] = data;
    }
    this._resetSelection();
    if (this.dataCount === 0) {
      this._setStatusNotification('noMatchingRecords');
    }
    if (this._notification) {
      this._publishNotification(this._notification);
    }
    if (this.notificationBarElement && this.notificationBarElement.nativeElement) {
      const ancestorContainers = this._scrollDispatcher.getAncestorScrollContainers(this.notificationBarElement);
      ancestorContainers.forEach(s => s.scrollTo({ top: 0, left: 0 }));
    }
  }

  private _resetSelection() {
    this._selectedCompanyTable = {};
    if (this._selectedCompanyCount !== 0) {
      this._selectedCompanyCount = 0;
      this._handleSelectionChanged();
    }
  }

  private _handleSelectionChanged() {
    this._cachedSelection = null;
  }

  private _handleError(error: any) {
    this._setStatusNotification('internalServerError');
  }

  isCompanySelected(data: PSGCompanyInfo): boolean {
    return this._selectedCompanyTable[data.companyId];
  }

  areAllCompaniesSelected(): boolean {
    return this.dataCount > 0 && this._selectedCompanyCount === this.dataCount;
  }

  areAnyCompanySelected(): boolean {
    return this._selectedCompanyCount > 0;
  }

  clickAllCompanies(selected: boolean) {
    // This is a work around for IE not supporting indeterminate checkbox state correctly.  If the checkbox is
    // clicked in indeterminate state we force it to select all to behave like other browsers.
    if (!this.areAllCompaniesSelected() && this.areAnyCompanySelected()) {
      this.selectAllCompanies(true);
    } else {
      this.selectAllCompanies(selected);
    }
  }

  // Pagination event in Table
  public onPaginatorChanged(event: unknown) {
    this._pageSubject.next((event as CustomEvent<PaginatorPageEvent>).detail.range);
  }

  canSendPrenoteFile(): boolean {
    return this._entitlementsService.isEntitled(['CreateEmployeeDdPrenoteFileRequest'])
    && this._featureToggleService.isFeatureEnabled('enableSendPrenoteFile');
  }

  sendPrenoteFile() {
    if (this.isSendingPrenote) {
      return;
    }
    this.sendPrenoteConfirmationModal.present();
  }

  onSubmit(): void {
    this.sendPrenoteConfirmationModal.dismiss();
    this.submitPrenoteFile();
  }

  submitPrenoteFile(): boolean {
    const selectedCompanies = this._getSelectedCompanies();

    if (this.isSendingPrenote) {
      return;
    }

    this.isSendingPrenote = true;
    this._prenoteRequestService.sendPrenote(this.settings.tenantId, selectedCompanies.map(c => c.companyId))
      .subscribe({
        error: (error: HttpErrorResponse) => {
          switch (error.status) {
            case 403:
              this._setStatusNotification('insufficientRights');
              break;
            default:
              this._setStatusNotification('internalServerError');
          }
          this._publishNotification(this._notification);
        },
        complete: () => {
          this.isSendingPrenote = false;
          this._refreshData();
          this.sendPrenoteResultsModal.present();
        }
      });
  }

  viewHistory() {
    this.router.navigate([{outlets: { eepay: 'prenotehistory'}}]);
  }

  clickCompany(data: PSGCompanyInfo, selected: boolean) {
    // This is a work around for IE not supporting indeterminate checkbox state correctly.  If the checkbox is
    // clicked in indeterminate state we set selected to true to behave like other browsers.
    this.selectCompany(data, selected);
  }

  selectAllCompanies(selected: boolean) {
    if (!this.rawData) {
      return;
    }

    const oldCount = this._selectedCompanyCount;
    this._selectedCompanyCount = selected ? this.rawData.length : 0;

    if (this._selectedCompanyCount !== oldCount) {
      for (const data of this.rawData) {
        this._selectedCompanyTable[data.companyId] = selected;
      }

      this._handleSelectionChanged();
    }
  }

  selectCompany(data: PSGCompanyInfo, selected: boolean) {
    let changed = false;

    if (selected) {
      if (!this._selectedCompanyTable[data.companyId]) {
        this._selectedCompanyTable[data.companyId] = true;
        this._selectedCompanyCount++;

        changed = true;
      }
    } else {
      if (this._selectedCompanyTable[data.companyId]) {
        this._selectedCompanyTable[data.companyId] = false;
        this._selectedCompanyCount--;

        changed = true;
      }
    }

    if (changed) {
      let selectedCount = 0;
      if (this._selectedCompanyTable[data.companyId]) {
        selectedCount++;
      }
      this._handleSelectionChanged();
    }
  }

  getRequestedFromKey(requestedFromPsg: boolean): string {
    return requestedFromPsg ? 'COMPANIES.PSG_REQUESTED' : 'COMPANIES.SSP_REQUESTED';
  }

  get selectedCompanies(): PSGCompanyInfo[] {
    if (!this._cachedSelection) {
      const results: PSGCompanyInfo[] = [];
      for (const id in this._selectedCompanyTable) {
        if (this._selectedCompanyTable.hasOwnProperty(id) && this._selectedCompanyTable[id]) {
          const item = this._rawDataTable[id];
          if (item) {
            results.push(item);
          }
        }
      }

      this._cachedSelection = results;
    }

    return this._cachedSelection;
  }

  private _getSelectedCompanies() {
    let selectedItems: PSGCompanyInfo[] = [];
    if (!GridDataSource.isEmptySort(this._sortSubject.value)) {
      selectedItems = this.dataSource.customSort(this.selectedCompanies, this._sortSubject.value);
    } else {
      // This could probably be replaced by:

      // selectedItems = this.selectedPayItems;

      // However, this story isn't about export, so I'm not making this change at this time.
      // -Abby Hill, 4/6/2022
      for (const data of this.rawData) {
        if (this._selectedCompanyTable.hasOwnProperty(data.companyId) && this._selectedCompanyTable[data.companyId]) {
          selectedItems.push(data);
        }
      }
    }
    return selectedItems;
  }
}
